/*
  Own review system: cut-off reviews (snippets/review-card.liquid, snippets/review-snippet.liquid). This file is OURS.

  - Every review item ([data-review-item]) has a clip area of maximum height ([data-review-clip]). Items whose content
    does not fit get "is-clipped" (fade-out and "Weiterlesen" come from CSS). Re-checked whenever a clip changes size,
    e.g. when "show more" reveals a card.
  - The product review grid (.review-grid) is laid out as masonry: three columns of cards with different heights.
  - Clicking a clipped item opens the theme drawer named by its "Weiterlesen" button. The full review is put into the
    drawer first: from the item's <template data-review-full> if it has one (the small carousel), otherwise a copy of
    the card itself. The theme opens the drawer on its own (any click on [aria-controls="<drawer id>"] does).
*/

if (!window.reviewCardsReady) {
  window.reviewCardsReady = true;

  const check = (clip) => {
    clip.closest('[data-review-item]')?.classList.toggle('is-clipped', clip.scrollHeight > clip.clientHeight + 2);
  };

  const observer = new ResizeObserver((entries) => entries.forEach((entry) => check(entry.target)));
  const observeAll = () => document.querySelectorAll('[data-review-clip]').forEach((clip) => {
    observer.observe(clip);
    check(clip);
  });

  // Masonry for the product review grid: every card spans as many 4px rows as it is tall (plus the gap), so cards of
  // different height interlock in their columns. Re-laid out whenever a card changes size: other column count,
  // "show more" revealing cards, a card getting clipped.
  const layoutQueue = new Set();
  const layout = () => {
    layoutQueue.forEach((grid) => {
      const style = getComputedStyle(grid);
      const row = parseFloat(style.gridAutoRows) || 4;
      const gap = parseFloat(style.columnGap) || 0;
      for (const card of grid.children) {
        if (card.hidden) continue;
        card.style.gridRowEnd = `span ${Math.max(1, Math.ceil((card.getBoundingClientRect().height + gap) / row))}`;
      }
    });
    layoutQueue.clear();
  };
  const masonryObserver = new ResizeObserver((entries) => {
    entries.forEach((entry) => layoutQueue.add(entry.target.parentElement));
    requestAnimationFrame(layout);
  });
  const setupMasonry = () => document.querySelectorAll('.review-grid').forEach((grid) => {
    grid.classList.add('is-masonry');
    for (const card of grid.children) masonryObserver.observe(card);
  });

  const init = () => {
    observeAll();
    setupMasonry();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);

  // Fill the drawer before the theme opens it (capture phase runs before the theme's own click handler).
  document.addEventListener('click', (event) => {
    const button = event.target.closest('.review-card__more');
    if (!button) return;
    const target = document.getElementById(button.getAttribute('aria-controls'))?.querySelector('.review-drawer__content');
    const item = button.closest('[data-review-item]');
    if (!target || !item) return;

    const template = item.querySelector('template[data-review-full]');
    const copy = template ? template.content.firstElementChild.cloneNode(true) : item.cloneNode(true);
    copy.classList.remove('is-clipped', 'rounded', 'snap-start', 'review-carousel__card');
    copy.classList.add('review-card--full');
    copy.removeAttribute('style');
    copy.removeAttribute('data-review-item');
    copy.querySelector('[data-review-clip]')?.removeAttribute('data-review-clip');
    copy.querySelector('.review-card__more')?.remove();
    target.replaceChildren(copy);
  }, true);

  // "Alle Bewertungen ansehen" and the star badge under the title: glide to the review section instead of jumping.
  // scroll-margin-top on the target keeps its heading clear of the sticky header.
  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href="#shopify-product-reviews"]');
    const target = document.getElementById('shopify-product-reviews');
    if (!link || !target) return;
    event.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });

  // The whole clipped item is clickable, not only "Weiterlesen".
  document.addEventListener('click', (event) => {
    const item = event.target.closest('[data-review-item].is-clipped');
    if (!item || event.target.closest('a, button')) return;
    item.querySelector('.review-card__more')?.click();
  });
}
